/**
 * Kunden (Customers) — operator-only Stammdatenverwaltung.
 *
 * Ein Kunde ist die kommerzielle/abrechnungsrelevante Entität. Ein Tenant ist
 * EIN Deployment; ein Kunde kann MEHRERE Deployments haben (Zuordnung je
 * Deployment in der Tenant-Detailansicht → PUT /api/tenants/:slug/customer).
 *
 * Diese Seite listet alle Kunden, erlaubt Anlegen (Inline-Form mit Stammdaten)
 * und verlinkt in die Kunden-Detailseite (Deployments + gebündeltes
 * Vertragsmanagement). Muster bewusst identisch zu ContextsPage.
 *
 * Backend: GET/POST /api/customers (customer.routes.ts).
 */
import { useEffect, useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  CustomerView,
  CustomerInput,
  createCustomer,
  listCustomers,
  extractApiError,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { labelForCustomerStatus } from '../services/status-labels';

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  archived: 'bg-slate-200 text-slate-600',
};

export function CustomersListPage() {
  const { operator } = useAuth();
  const isReadOnly = !!operator?.readOnly;
  const [rows, setRows] = useState<CustomerView[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function refresh(): void {
    setLoading(true);
    listCustomers()
      .then(setRows)
      .catch((err) => setError(extractApiError(err)))
      .finally(() => setLoading(false));
  }

  useEffect(() => { refresh(); }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">Kunden ({rows.length})</h2>
          <p className="text-sm text-slate-500">
            Kommerzielle Stammdaten. Ein Kunde kann mehrere Deployments
            (Tenants) besitzen; die Zuordnung erfolgt je Deployment in der
            Tenant-Detailansicht.
          </p>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="btn-primary"
          disabled={isReadOnly}
        >
          {showForm ? 'Schliessen' : 'Kunde anlegen'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {showForm && !isReadOnly && (
        <CreateForm
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); refresh(); }}
          onError={setError}
        />
      )}

      {loading ? <p className="text-slate-500">Lade…</p> : (
        <table className="w-full bg-white shadow rounded">
          <thead className="bg-slate-100 text-left text-sm uppercase tracking-wide">
            <tr>
              <th className="p-3">Firma</th>
              <th className="p-3">USt-ID</th>
              <th className="p-3">Marke</th>
              <th className="p-3">Status</th>
              <th className="p-3">Deployments</th>
              <th className="p-3">Angelegt</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {rows.map((c) => (
              <tr key={c.id} className="border-t hover:bg-slate-50">
                <td className="p-3 font-medium">
                  <Link to={`/customers/${c.id}`} className="text-primary-600 hover:underline">
                    {c.companyName}
                  </Link>
                  {c.displayName && <span className="text-slate-400"> · {c.displayName}</span>}
                </td>
                <td className="p-3 font-mono text-slate-600">{c.vatId ?? '—'}</td>
                <td className="p-3 font-mono text-slate-600">{c.brand}</td>
                <td className="p-3">
                  <span className={`badge ${STATUS_COLORS[c.status] ?? 'bg-slate-100 text-slate-500'}`}>
                    {labelForCustomerStatus(c.status)}
                  </span>
                </td>
                <td className="p-3 text-slate-600">{c.tenantCount}</td>
                <td className="p-3 text-slate-500" title={c.createdAt}>
                  {new Date(c.createdAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">
                Noch keine Kunden angelegt.
              </td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface CreateFormProps {
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}

const BRAND_RE = /^[a-z0-9][a-z0-9-]{0,30}[a-z0-9]$/;

function CreateForm({ onClose, onSaved, onError }: CreateFormProps) {
  const [form, setForm] = useState<CustomerInput>({ companyName: '', brand: 'sectepe' });
  const [busy, setBusy] = useState(false);

  const companyValid = (form.companyName ?? '').trim().length > 0 && (form.companyName ?? '').length <= 160;
  const brandValid = BRAND_RE.test(form.brand ?? 'sectepe');

  function set<K extends keyof CustomerInput>(key: K, value: CustomerInput[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!companyValid || !brandValid) {
      onError('Firma (1–160 Zeichen) und Marke (Kleinbuchstaben-Slug) erforderlich.');
      return;
    }
    setBusy(true);
    try {
      await createCustomer(form);
      onSaved();
    } catch (err) {
      onError(extractApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="bg-white shadow rounded p-4 mb-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Kunde anlegen</h3>
        <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-700">×</button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Firma *">
          <input
            value={form.companyName ?? ''}
            onChange={(e) => set('companyName', e.target.value)}
            placeholder="z.B. Acme GmbH"
            maxLength={160}
            className="w-full px-2 py-1 rounded border text-sm"
            required
          />
        </Field>
        <Field label="Anzeigename (optional)">
          <input
            value={form.displayName ?? ''}
            onChange={(e) => set('displayName', e.target.value)}
            className="w-full px-2 py-1 rounded border text-sm"
          />
        </Field>
        <Field label="USt-ID (optional)">
          <input
            value={form.vatId ?? ''}
            onChange={(e) => set('vatId', e.target.value.toUpperCase())}
            placeholder="DE123456789"
            className="w-full px-2 py-1 rounded border font-mono text-sm"
          />
        </Field>
        <Field label="Marke">
          <input
            value={form.brand ?? 'sectepe'}
            onChange={(e) => set('brand', e.target.value.toLowerCase())}
            className="w-full px-2 py-1 rounded border font-mono text-sm"
            required
          />
          {!brandValid && <p className="text-xs text-red-600 mt-1">Kleinbuchstaben-Slug (a-z0-9-, 2–32).</p>}
        </Field>
        <Field label="Adresse (Zeile 1)">
          <input value={form.addressLine1 ?? ''} onChange={(e) => set('addressLine1', e.target.value)} className="w-full px-2 py-1 rounded border text-sm" />
        </Field>
        <Field label="Adresse (Zeile 2)">
          <input value={form.addressLine2 ?? ''} onChange={(e) => set('addressLine2', e.target.value)} className="w-full px-2 py-1 rounded border text-sm" />
        </Field>
        <Field label="PLZ">
          <input value={form.postalCode ?? ''} onChange={(e) => set('postalCode', e.target.value)} className="w-full px-2 py-1 rounded border text-sm" />
        </Field>
        <Field label="Ort">
          <input value={form.city ?? ''} onChange={(e) => set('city', e.target.value)} className="w-full px-2 py-1 rounded border text-sm" />
        </Field>
        <Field label="Land (ISO-2)">
          <input value={form.country ?? ''} onChange={(e) => set('country', e.target.value.toUpperCase())} placeholder="DE" maxLength={2} className="w-full px-2 py-1 rounded border font-mono text-sm" />
        </Field>
        <Field label="Rechnungs-E-Mail">
          <input type="email" value={form.billingEmail ?? ''} onChange={(e) => set('billingEmail', e.target.value)} placeholder="billing@acme.de" className="w-full px-2 py-1 rounded border text-sm" />
        </Field>
        <Field label="Rechnungskontakt">
          <input value={form.billingContact ?? ''} onChange={(e) => set('billingContact', e.target.value)} className="w-full px-2 py-1 rounded border text-sm" />
        </Field>
        <Field label="Externe Referenz (CRM/Commerce)">
          <input value={form.externalRef ?? ''} onChange={(e) => set('externalRef', e.target.value)} maxLength={128} className="w-full px-2 py-1 rounded border text-sm" />
        </Field>
      </div>

      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onClose} className="px-3 py-1 rounded border" disabled={busy}>Abbrechen</button>
        <button type="submit" className="btn-primary" disabled={busy || !companyValid || !brandValid}>
          {busy ? 'Speichere…' : 'Speichern'}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
    </div>
  );
}
